import React from 'react';
import { Action, UserType } from '../common.js';
import {
    ConsentUpdate,
    LegitimateInterestUpdate,
} from '../api/consent-sync-receiver.js';
import { toast, Toaster } from 'react-hot-toast';
import {
    trackUpdateConsent,
    trackUpdateLegitimateInterest,
} from './consent-tracking.js';
import { I18n } from '@lingui/core';

export type ConsentContextType = {
    updateConsent: (consent: GivenConsent, action: Action) => Promise<void>;
    updateLegitimateInterest: (
        legitimateInterestForUser: LegitimateInterestForUser,
        action: Action,
    ) => Promise<void>;
    consentUser: ConsentUser;
    givenConsents: GivenConsents;
};

export const ConsentContext = React.createContext<ConsentContextType>({
    updateConsent: (consent: GivenConsent, action: Action) => Promise.resolve(),
    updateLegitimateInterest: (
        legitimateInterestForUser: LegitimateInterestForUser,
        action: Action,
    ) => Promise.resolve(),
    consentUser: {
        userId: '0',
        userType: UserType.NOT_LOGGED_IN,
        isLoggedIn: false,
    },
    givenConsents: { consents: [], legitimateInterests: [] },
});

export type ContextProps = {
    baseUrl: string;
    givenConsents: GivenConsents;
    consentUser: ConsentUser;
    children: React.ReactNode;
    hmacToken: string;
    i18n: I18n;
};

export type EventData = {
    eventName: string;
    accept: 'Subscribe' | 'Accept';
    reject: 'Unsubscribe' | 'Reject';
};

export function ConsentContextProvider(
    props: ContextProps,
): React.ReactElement {
    const handleError = (error: Error) => {
        console.error(error);
        toast.error(
            props.i18n.t({
                id: 'privacy-settings.toast.error',
                comment:
                    'Error message shown to the user when something goes wrong',
            }),
        );
    };
    const updateConsent = (
        consent: GivenConsent,
        action: Action,
    ): Promise<void> => {
        console.log(`Update consent for ${JSON.stringify(props.consentUser)}`);

        return fetch(`${props.baseUrl}/consent`, {
            credentials: 'same-origin',
            method: 'POST',
            body: JSON.stringify({
                definition: consent.definition,
                action,
            } as ConsentUpdate),
            headers: {
                'Content-Type': 'application/json',
                'x-csrf-token': props.hmacToken,
            },
        })
            .then((response) => {
                if (!response.ok) {
                    throw new Error('ERROR');
                }
                toast.success(
                    props.i18n.t({
                        id: 'privacy-settings.toast.success',
                        comment:
                            'Success message shown to the user when the consent is updated',
                    }),
                );
            })
            .then(() => trackUpdateConsent(consent, action))
            .catch(handleError);
    };

    const updateLegitimateInterest = (
        legitimateInterestForUser: LegitimateInterestForUser,
        action: Action,
    ): Promise<void> => {
        return fetch(`${props.baseUrl}/legitimate-interest`, {
            credentials: 'same-origin',
            method: 'POST',
            body: JSON.stringify({
                purposeName: legitimateInterestForUser.purpose.name,
                action,
            } as LegitimateInterestUpdate),
            headers: {
                'Content-Type': 'application/json',
                'x-csrf-token': props.hmacToken,
            },
        })
            .then((response) => {
                if (!response.ok) {
                    throw new Error('ERROR');
                }
                toast.success(
                    props.i18n.t({
                        id: 'privacy-settings.toast.success',
                        comment:
                            'Success message shown to the user when the consent is updated',
                    }),
                );
            })
            .then(() =>
                trackUpdateLegitimateInterest(
                    legitimateInterestForUser,
                    action,
                ),
            )
            .catch(handleError);
    };
    return (
        <ConsentContext.Provider
            value={{
                consentUser: props.consentUser,
                givenConsents: props.givenConsents,
                updateConsent,
                updateLegitimateInterest,
            }}
        >
            <Toaster />
            {props.children}
        </ConsentContext.Provider>
    );
}
